import { z } from 'zod';

import { fromPostgrest, guard, raise, type Result } from '@/lib/result';
import { supabase } from '@/lib/supabase';

import {
  mapSupportTicket,
  type SupportTicket,
  type SupportTicketCategory,
  type SupportTicketPriority,
  type SupportTicketRole,
  type SupportTicketStatus,
} from '../model/support-ticket';
import {
  mapSupportTicketMessage,
  type SupportTicketMessage,
} from '../model/support-ticket-message';

const TICKET_COLS =
  'id, created_by, role_at_creation, subject, category, status, priority, assigned_admin_id, related_application_id, related_masjid_id, created_at, updated_at, first_response_at, resolved_at';
const TICKET_SELECT = `${TICKET_COLS}, creator:profiles!created_by(full_name), assignee:profiles!assigned_admin_id(full_name)`;
const MESSAGE_SELECT =
  'id, ticket_id, sender_id, body, created_at, is_internal, sender:profiles!sender_id(full_name)';
const ADMIN_SELECT = 'id, full_name';

const adminRowSchema = z.object({ id: z.string(), full_name: z.string() });

export interface AdminOption {
  readonly id: string;
  readonly fullName: string;
}

export interface CreateTicketInput {
  readonly subject: string;
  readonly body: string;
  readonly roleAtCreation: SupportTicketRole;
  readonly priority: SupportTicketPriority;
  readonly category: SupportTicketCategory;
  readonly relatedApplicationId: string | null;
  readonly relatedMasjidId: string | null;
}

export interface QueueFilter {
  readonly status?: SupportTicketStatus | 'all';
  readonly priority?: SupportTicketPriority | 'all';
  readonly category?: SupportTicketCategory | 'all';
  readonly assignedToMe?: boolean;
  readonly currentUserId?: string;
  /** Case-insensitive subject substring match. */
  readonly search?: string;
}

export const supportRepository = {
  async listMine(userId: string): Promise<Result<readonly SupportTicket[]>> {
    return guard(async () => {
      const { data, error } = await supabase
        .from('support_tickets')
        .select(TICKET_SELECT)
        .eq('created_by', userId)
        .order('created_at', { ascending: false });
      if (error) throw fromPostgrest(error);
      return ((data ?? []) as unknown[]).map(mapSupportTicket);
    });
  },

  /**
   * Admin-only in effect (RLS returns nothing for non-admins). Paginated via
   * `.range` — `page` is 0-based; callers detect "no more pages" by getting
   * back fewer than `pageSize` rows (see `useTicketQueue`'s `useInfiniteQuery`).
   */
  async listQueue(
    filter: QueueFilter = {},
    page = 0,
    pageSize = 20,
  ): Promise<Result<readonly SupportTicket[]>> {
    return guard(async () => {
      let q = supabase.from('support_tickets').select(TICKET_SELECT);
      if (filter.status && filter.status !== 'all') q = q.eq('status', filter.status);
      if (filter.priority && filter.priority !== 'all') q = q.eq('priority', filter.priority);
      if (filter.category && filter.category !== 'all') q = q.eq('category', filter.category);
      if (filter.assignedToMe && filter.currentUserId) {
        q = q.eq('assigned_admin_id', filter.currentUserId);
      }
      const search = filter.search?.trim();
      if (search) q = q.ilike('subject', `%${search}%`);
      const from = page * pageSize;
      const to = from + pageSize - 1;
      const { data, error } = await q.order('created_at', { ascending: false }).range(from, to);
      if (error) throw fromPostgrest(error);
      return ((data ?? []) as unknown[]).map(mapSupportTicket);
    });
  },

  async get(id: string): Promise<Result<SupportTicket>> {
    return guard(async () => {
      const { data, error } = await supabase
        .from('support_tickets')
        .select(TICKET_SELECT)
        .eq('id', id)
        .single();
      if (error) throw fromPostgrest(error);
      return mapSupportTicket(data as unknown);
    });
  },

  async findByApplication(applicationId: string): Promise<Result<SupportTicket | null>> {
    return guard(async () => {
      const { data, error } = await supabase
        .from('support_tickets')
        .select(TICKET_SELECT)
        .eq('related_application_id', applicationId)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw fromPostgrest(error);
      return data ? mapSupportTicket(data as unknown) : null;
    });
  },

  async listMessages(ticketId: string): Promise<Result<readonly SupportTicketMessage[]>> {
    return guard(async () => {
      const { data, error } = await supabase
        .from('support_ticket_messages')
        .select(MESSAGE_SELECT)
        .eq('ticket_id', ticketId)
        .order('created_at', { ascending: true });
      if (error) throw fromPostgrest(error);
      return ((data ?? []) as unknown[]).map(mapSupportTicketMessage);
    });
  },

  async create(input: CreateTicketInput): Promise<Result<string>> {
    return guard(async () => {
      const { data, error } = await supabase.rpc('create_support_ticket', {
        p_subject: input.subject,
        p_body: input.body,
        p_role_at_creation: input.roleAtCreation,
        p_priority: input.priority,
        p_category: input.category,
        p_related_application_id: input.relatedApplicationId,
        p_related_masjid_id: input.relatedMasjidId,
      });
      if (error) raise('server', error.message, error);
      if (typeof data !== 'string') raise('server', 'create_support_ticket returned no id');
      return data;
    });
  },

  async reply(ticketId: string, body: string): Promise<Result<void>> {
    return guard(async () => {
      const { error } = await supabase.rpc('reply_support_ticket', {
        p_ticket_id: ticketId,
        p_body: body,
      });
      if (error) raise('server', error.message, error);
    });
  },

  async assign(ticketId: string, adminId: string | null): Promise<Result<void>> {
    return guard(async () => {
      const { error } = await supabase.rpc('assign_support_ticket', {
        p_ticket_id: ticketId,
        p_admin_id: adminId,
      });
      if (error) raise('server', error.message, error);
    });
  },

  async updateStatus(
    ticketId: string,
    status: SupportTicketStatus,
    note: string | null,
  ): Promise<Result<void>> {
    return guard(async () => {
      const { error } = await supabase.rpc('update_ticket_status', {
        p_ticket_id: ticketId,
        p_status: status,
        p_note: note,
      });
      if (error) raise('server', error.message, error);
    });
  },

  /** Admin-only; notifies the creator (see set_ticket_priority RPC). */
  async setPriority(ticketId: string, priority: SupportTicketPriority): Promise<Result<void>> {
    return guard(async () => {
      const { error } = await supabase.rpc('set_ticket_priority', {
        p_ticket_id: ticketId,
        p_priority: priority,
      });
      if (error) raise('server', error.message, error);
    });
  },

  /** Creator or admin; only from resolved/closed (enforced server-side). */
  async reopen(ticketId: string): Promise<Result<void>> {
    return guard(async () => {
      const { error } = await supabase.rpc('reopen_support_ticket', {
        p_ticket_id: ticketId,
      });
      if (error) raise('server', error.message, error);
    });
  },

  /** Admin-only; inserted as `is_internal = true` — RLS hides it from the creator. */
  async addInternalNote(ticketId: string, body: string): Promise<Result<string>> {
    return guard(async () => {
      const { data, error } = await supabase.rpc('add_internal_note', {
        p_ticket_id: ticketId,
        p_body: body,
      });
      if (error) raise('server', error.message, error);
      if (typeof data !== 'string') raise('server', 'add_internal_note returned no id');
      return data;
    });
  },

  /** Admin-only; returns the number of tickets updated. */
  async bulkUpdateStatus(
    ids: readonly string[],
    status: SupportTicketStatus,
  ): Promise<Result<number>> {
    return guard(async () => {
      const { data, error } = await supabase.rpc('bulk_update_ticket_status', {
        p_ids: ids,
        p_status: status,
      });
      if (error) raise('server', error.message, error);
      if (typeof data !== 'number') raise('server', 'bulk_update_ticket_status returned no count');
      return data;
    });
  },

  /** Every admin profile, for the assignment picker. */
  async listAdmins(): Promise<Result<readonly AdminOption[]>> {
    return guard(async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select(ADMIN_SELECT)
        .eq('is_admin', true)
        .order('full_name', { ascending: true });
      if (error) throw fromPostgrest(error);
      return ((data ?? []) as unknown[])
        .map((row) => adminRowSchema.parse(row))
        .map((r) => ({ id: r.id, fullName: r.full_name }));
    });
  },

  /** Live-updates for a ticket's message thread (same pattern as chat). */
  subscribeMessages(ticketId: string, onInsert: () => void): () => void {
    const channel = supabase
      .channel(`support-ticket:${ticketId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'support_ticket_messages',
          filter: `ticket_id=eq.${ticketId}`,
        },
        () => onInsert(),
      )
      .subscribe();
    return () => {
      void supabase.removeChannel(channel);
    };
  },
};
